"use client";

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    LineChart,
    Line,
    Legend,
    PieChart,
    Pie,
    Cell,
} from "recharts";
import {
    FileText,
    FolderOpen,
    CheckCircle2,
    AlertTriangle,
    Star,
    TrendingUp,
    Clock,
    Users,
} from "lucide-react";
import { getDashboardStats, getMonthlyTrendData } from "@/lib/mock-data";

export default function AdminDashboardPage() {
    const stats = getDashboardStats();
    const trendData = getMonthlyTrendData();

    const statCards = [
        {
            title: "เรื่องทั้งหมด",
            value: stats.total,
            icon: FileText,
            color: "#1e3a5f",
            bgColor: "#e0e7ef",
        },
        {
            title: "เปิดค้าง",
            value: stats.submitted + stats.inProgress + stats.pendingInfo,
            icon: FolderOpen,
            color: "#ca8a04",
            bgColor: "#fef9c3",
        },
        {
            title: "ปิดเดือนนี้",
            value: stats.closedThisMonth,
            icon: CheckCircle2,
            color: "#16a34a",
            bgColor: "#dcfce7",
        },
        {
            title: "ใกล้เกิน SLA",
            value: stats.nearSla,
            icon: AlertTriangle,
            color: "#ea580c",
            bgColor: "#ffedd5",
        },
        {
            title: "เกิน SLA",
            value: stats.overdue,
            icon: AlertTriangle,
            color: "#dc2626",
            bgColor: "#fee2e2",
        },
        {
            title: "ความพึงพอใจ",
            value: "4.8",
            suffix: "/5",
            icon: Star,
            color: "#c9a227",
            bgColor: "#fef3c7",
        },
    ];

    const departmentData = [
        { name: "กองสืบสวนฯ", received: 45, closed: 42 },
        { name: "กองตรวจสินค้า", received: 38, closed: 35 },
        { name: "กองพิธีการฯ", received: 28, closed: 25 },
        { name: "กองกฎหมาย", received: 15, closed: 14 },
        { name: "กองบริหาร", received: 8, closed: 8 },
    ];

    const slaData = [
        { name: "ตอบใน SLA", value: 85, fill: "#16a34a" },
        { name: "เกิน SLA", value: 15, fill: "#dc2626" },
    ];

    return (
        <div className="space-y-6">
            {/* Page Header */}
            <div>
                <h1 className="heading-sm text-primary">แดชบอร์ดผู้บริหาร</h1>
                <p className="text-muted-foreground">ภาพรวมระบบร้องเรียนสำหรับผู้ดูแลระบบ</p>
            </div>

            {/* Stat Cards - Improved mobile layout */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
                {statCards.map((stat) => (
                    <Card key={stat.title} className="relative overflow-hidden">
                        <CardContent className="p-4 sm:pt-6">
                            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                                <div
                                    className="h-10 w-10 sm:h-12 sm:w-12 shrink-0 rounded-lg flex items-center justify-center"
                                    style={{ backgroundColor: stat.bgColor }}
                                >
                                    <stat.icon className="h-5 w-5 sm:h-6 sm:w-6" style={{ color: stat.color }} />
                                </div>
                                <div>
                                    <p className="text-xl sm:text-2xl font-bold">
                                        {stat.value}
                                        {stat.suffix && <span className="text-xs sm:text-sm font-normal text-muted-foreground">{stat.suffix}</span>}
                                    </p>
                                    <p className="text-xs sm:text-sm text-muted-foreground">{stat.title}</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </div>

            {/* Charts Row */}
            <div className="grid lg:grid-cols-2 gap-6">
                {/* Trend Chart */}
                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-base">แนวโน้มรับ-ปิดเรื่อง</CardTitle>
                        <CardDescription className="text-xs sm:text-sm">เปรียบเทียบจำนวนเรื่องรับใหม่และปิดในแต่ละเดือน</CardDescription>
                    </CardHeader>
                    <CardContent className="px-2 sm:px-6">
                        <div className="h-[240px] sm:h-[280px]">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={trendData} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                                    <YAxis tick={{ fontSize: 11 }} width={30} />
                                    <Tooltip />
                                    <Legend wrapperStyle={{ fontSize: '12px' }} />
                                    <Line
                                        type="monotone"
                                        dataKey="received"
                                        name="รับเรื่อง"
                                        stroke="#1e3a5f"
                                        strokeWidth={2}
                                        dot={{ fill: "#1e3a5f", r: 3 }}
                                    />
                                    <Line
                                        type="monotone"
                                        dataKey="closed"
                                        name="ปิดเรื่อง"
                                        stroke="#16a34a"
                                        strokeWidth={2}
                                        dot={{ fill: "#16a34a", r: 3 }}
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </CardContent>
                </Card>

                {/* SLA Performance - Fixed mobile layout */}
                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-base">ประสิทธิภาพ SLA</CardTitle>
                        <CardDescription className="text-xs sm:text-sm">สัดส่วนการตอบสนองภายในเวลาที่กำหนด</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8">
                            <div className="relative">
                                <ResponsiveContainer width={160} height={160}>
                                    <PieChart>
                                        <Pie
                                            data={slaData}
                                            cx="50%"
                                            cy="50%"
                                            innerRadius={50}
                                            outerRadius={75}
                                            paddingAngle={2}
                                            dataKey="value"
                                        >
                                            {slaData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={entry.fill} />
                                            ))}
                                        </Pie>
                                    </PieChart>
                                </ResponsiveContainer>
                                <div className="absolute inset-0 flex flex-col items-center justify-center">
                                    <span className="text-2xl sm:text-3xl font-bold text-green-600">85%</span>
                                    <span className="text-xs sm:text-sm text-muted-foreground">ใน SLA</span>
                                </div>
                            </div>
                            <div className="flex sm:flex-col gap-4 sm:gap-2">
                                {slaData.map((item) => (
                                    <div key={item.name} className="flex items-center gap-2">
                                        <div className="h-3 w-3 rounded-full" style={{ backgroundColor: item.fill }} />
                                        <span className="text-xs sm:text-sm">{item.name}</span>
                                        <Badge variant="secondary" className="text-xs">{item.value}%</Badge>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Department Comparison */}
            <Card>
                <CardHeader className="pb-2">
                    <CardTitle className="text-base">เปรียบเทียบตามหน่วยงาน</CardTitle>
                    <CardDescription className="text-xs sm:text-sm">จำนวนเรื่องที่รับและปิดแยกตามหน่วยงาน</CardDescription>
                </CardHeader>
                <CardContent className="px-2 sm:px-6">
                    <div className="h-[280px] sm:h-[300px]">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={departmentData} layout="vertical" margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                                <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
                                <XAxis type="number" tick={{ fontSize: 11 }} />
                                <YAxis dataKey="name" type="category" width={80} tick={{ fontSize: 10 }} />
                                <Tooltip />
                                <Legend wrapperStyle={{ fontSize: '12px' }} />
                                <Bar dataKey="received" name="รับเรื่อง" fill="#1e3a5f" radius={[0, 4, 4, 0]} />
                                <Bar dataKey="closed" name="ปิดเรื่อง" fill="#16a34a" radius={[0, 4, 4, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </CardContent>
            </Card>

            {/* Recent Activity Summary - Improved mobile cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
                <Card>
                    <CardContent className="pt-6">
                        <div className="flex items-center gap-4">
                            <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center shrink-0">
                                <TrendingUp className="h-6 w-6 text-green-600" />
                            </div>
                            <div>
                                <p className="text-2xl sm:text-3xl font-bold text-primary">12</p>
                                <p className="text-sm font-medium">เรื่องรับใหม่วันนี้</p>
                                <p className="text-xs text-muted-foreground">เพิ่มขึ้น 20% จากเมื่อวาน</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card>
                    <CardContent className="pt-6">
                        <div className="flex items-center gap-4">
                            <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                                <Clock className="h-6 w-6 text-blue-600" />
                            </div>
                            <div>
                                <p className="text-2xl sm:text-3xl font-bold text-primary">2.4 <span className="text-lg font-normal">วัน</span></p>
                                <p className="text-sm font-medium">เฉลี่ยเวลาปิดเรื่อง</p>
                                <p className="text-xs text-muted-foreground">ดีกว่าเป้าหมาย 3 วัน</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card>
                    <CardContent className="pt-6">
                        <div className="flex items-center gap-4">
                            <div className="h-12 w-12 rounded-full bg-purple-100 flex items-center justify-center shrink-0">
                                <Users className="h-6 w-6 text-purple-600" />
                            </div>
                            <div>
                                <p className="text-2xl sm:text-3xl font-bold text-primary">24</p>
                                <p className="text-sm font-medium">ผู้ใช้งานในระบบ</p>
                                <p className="text-xs text-muted-foreground">เจ้าหน้าที่ 20 / ผู้ดูแล 4</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}

